import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  listVolunteers,
  listRickshaws,
  listMyBookings,
  appendBooking,
  deleteBookingForUser,
} from "@/lib/yatra-db";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import {
  Bike,
  UserCheck,
  Phone,
  X,
  Check,
  FileText,
  Calendar,
  Users,
  Plus,
  Minus,
  MapPin,
  Sparkles,
} from "lucide-react";
import { fmtDateOnly } from "@/lib/format-date";

type Volunteer = {
  id: string;
  name: string;
  phone: string;
  languages: string[];
  experience_years: number;
  charges_per_day: number;
  bio_en: string | null;
};
type Rickshaw = {
  id: string;
  driver_name: string;
  phone: string;
  vehicle_number: string;
  area: string | null;
  charges_per_trip: number;
};
type Booking = {
  id: string;
  type: "volunteer" | "rickshaw" | "visit";
  booking_date: string;
  booking_time: string;
  status: "pending" | "confirmed" | "completed" | "cancelled";
  volunteer_id: string | null;
  rickshaw_id: string | null;
  notes: string | null;
  group_size?: number;
  visitor_names?: string | null;
  provider_name?: string;
};

export const Route = createFileRoute("/app/bookings")({ component: BookingsScreen });

const BADGE: Record<string, string> = {
  confirmed: "bg-primary/12 text-primary border-primary/25",
  pending: "bg-primary/8 text-primary border-primary/20",
  completed: "bg-secondary text-foreground border-border",
  cancelled: "bg-destructive/10 text-destructive border-destructive/25",
};

function BookingsScreen() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState<"my" | "visit" | "rickshaw" | "volunteer">("my");
  const [vols, setVols] = useState<Volunteer[]>([]);
  const [rks, setRks] = useState<Rickshaw[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [confirmModal, setConfirmModal] = useState<{
    kind: "volunteer" | "rickshaw";
    id: string;
    name: string;
    phone: string;
  } | null>(null);
  const [notesModal, setNotesModal] = useState<{
    kind: "volunteer" | "rickshaw";
    id: string;
    name: string;
  } | null>(null);

  const load = async () => {
    setVols(listVolunteers(true) as Volunteer[]);
    setRks(listRickshaws(true) as Rickshaw[]);
    setBookings((user?.id ? listMyBookings(user.id) : []) as Booking[]);
  };
  useEffect(() => {
    load();
  }, [user?.id]);

  const TABS = [
    { k: "my" as const, label: t("myBookings"), icon: Calendar },
    { k: "visit" as const, label: "Visit", icon: MapPin },
    { k: "rickshaw" as const, label: "Rickshaws", icon: Bike },
    { k: "volunteer" as const, label: "Volunteers", icon: UserCheck },
  ];

  const findName = (b: Booking) => {
    if (b.provider_name) {
      return b.provider_name;
    }
    if (b.volunteer_id) {
      return vols.find((v) => v.id === b.volunteer_id)?.name || "Volunteer";
    }
    if (b.rickshaw_id) {
      return rks.find((r) => r.id === b.rickshaw_id)?.driver_name || "Rickshaw";
    }
    if (b.type === "visit") {
      return "Temple Visit";
    }
    return "—";
  };

  return (
    <div className="px-4 pt-14 pb-24 sm:px-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("bookings")}
          </div>
          <h1 className="font-serif text-2xl font-bold leading-tight">Plan your Yatra</h1>
        </div>
        <span className="rounded-full border border-border bg-card px-3 py-1 text-[11px] font-semibold text-muted-foreground">
          {bookings.length} total
        </span>
      </div>

      <div className="mb-5 grid grid-cols-4 gap-1 rounded-2xl border border-border bg-card p-1 shadow-sm">
        {TABS.map((tt) => {
          const active = tab === tt.k;
          return (
            <button
              key={tt.k}
              onClick={() => setTab(tt.k)}
              className={`flex flex-col items-center gap-1 rounded-xl py-2 text-[10px] font-semibold transition-all ${active ? "text-primary-foreground shadow-sm" : "text-muted-foreground"}`}
              style={active ? { background: "var(--gradient-saffron)" } : undefined}
            >
              <tt.icon className="h-4 w-4" />
              {tt.label}
            </button>
          );
        })}
      </div>

      {tab === "my" && (
        <MyBookings userId={user?.id} bookings={bookings} findName={findName} onRefresh={load} />
      )}
      {tab === "visit" && (
        <VisitForm
          userId={user?.id}
          onCreated={() => {
            load();
            setTab("my");
          }}
        />
      )}
      {tab === "rickshaw" && (
        <RickshawFeed
          list={rks}
          onConfirm={(r) =>
            setConfirmModal({ kind: "rickshaw", id: r.id, name: r.driver_name, phone: r.phone })
          }
          onNotes={(r) => setNotesModal({ kind: "rickshaw", id: r.id, name: r.driver_name })}
        />
      )}
      {tab === "volunteer" && (
        <VolunteerFeed
          list={vols}
          onConfirm={(v) =>
            setConfirmModal({ kind: "volunteer", id: v.id, name: v.name, phone: v.phone })
          }
          onNotes={(v) => setNotesModal({ kind: "volunteer", id: v.id, name: v.name })}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          userId={user?.id}
          data={confirmModal}
          onClose={() => setConfirmModal(null)}
          onDone={() => {
            setConfirmModal(null);
            load();
            setTab("my");
          }}
        />
      )}
      {notesModal && (
        <NotesModal
          userId={user?.id}
          data={notesModal}
          onClose={() => setNotesModal(null)}
          onDone={() => {
            setNotesModal(null);
            load();
            setTab("my");
          }}
        />
      )}
    </div>
  );
}

/* ---------------- My Bookings ---------------- */
function MyBookings({
  userId,
  bookings,
  findName,
  onRefresh,
}: {
  userId?: string;
  bookings: Booking[];
  findName: (b: Booking) => string;
  onRefresh: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const summary = useMemo(
    () => ({
      today: bookings.filter((b) => b.booking_date === today).length,
      upcoming: bookings.filter((b) => b.booking_date > today).length,
      confirmed: bookings.filter((b) => b.status === "confirmed").length,
    }),
    [bookings, today],
  );

  const cancel = (id: string) => {
    if (!userId) {
      return;
    }
    deleteBookingForUser(id, userId);
    toast.success("Booking removed");
    onRefresh();
  };

  if (bookings.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
        <div
          className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full"
          style={{ background: "var(--gradient-warm)" }}
        >
          <Calendar className="h-6 w-6 text-primary" />
        </div>
        <div className="font-serif text-lg font-bold">No bookings yet</div>
        <p className="mt-1 text-xs text-muted-foreground">
          Book a temple visit, rickshaw or volunteer guide to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        <SummaryCard label="Today" value={summary.today} tone="primary" />
        <SummaryCard label="Upcoming" value={summary.upcoming} tone="muted" />
        <SummaryCard label="Confirmed" value={summary.confirmed} tone="maroon" />
      </div>
      <div className="space-y-3">
        {bookings.map((b) => (
          <div
            key={b.id}
            className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition hover:shadow-md"
          >
            <div className="flex items-start gap-3 p-4">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                style={{ background: "var(--gradient-warm)" }}
              >
                {b.type === "rickshaw" ? (
                  <Bike className="h-5 w-5 text-primary" />
                ) : b.type === "volunteer" ? (
                  <UserCheck className="h-5 w-5 text-primary" />
                ) : (
                  <MapPin className="h-5 w-5 text-primary" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="truncate font-semibold">{findName(b)}</div>
                  <span
                    className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${BADGE[b.status]}`}
                  >
                    {b.status}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] capitalize text-muted-foreground">
                  {b.type} · {fmtDateOnly(b.booking_date)} · {b.booking_time?.slice(0, 5)}
                  {b.group_size && b.group_size > 1 ? ` · ${b.group_size} visitors` : ""}
                </div>
                {b.notes && (
                  <div className="mt-2 rounded-lg bg-muted/50 px-2 py-1.5 text-[11px] text-muted-foreground">
                    <FileText className="mr-1 inline h-3 w-3" />
                    {b.notes}
                  </div>
                )}
              </div>
            </div>
            <div className="flex border-t border-border text-xs font-semibold">
              <button
                onClick={() => cancel(b.id)}
                className="flex-1 py-2.5 text-destructive hover:bg-destructive/5"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "primary" | "muted" | "maroon";
}) {
  const map = {
    primary: "bg-primary/10 text-primary",
    muted: "bg-muted text-foreground",
    maroon: "bg-maroon/10 text-maroon",
  }[tone];
  return (
    <div className={`rounded-2xl border border-border p-3 ${map}`}>
      <div className="font-serif text-2xl font-bold leading-none">{value}</div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider opacity-80">
        {label}
      </div>
    </div>
  );
}

/* ---------------- Visit Booking Form ---------------- */
function VisitForm({ userId, onCreated }: { userId?: string; onCreated: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [slot, setSlot] = useState("08:00");
  const [size, setSize] = useState(1);
  const [members, setMembers] = useState<string[]>([""]);
  const [notes, setNotes] = useState("");
  const isGroup = size > 4;

  const SLOTS = [
    { k: "06:00", label: "Early Morning", sub: "6 – 8 AM" },
    { k: "08:00", label: "Morning", sub: "8 – 11 AM" },
    { k: "14:00", label: "Afternoon", sub: "2 – 5 PM" },
    { k: "17:00", label: "Evening", sub: "5 – 7 PM" },
  ];

  // Keep members array length in sync with size
  const syncSize = (n: number) => {
    const clamped = Math.max(1, Math.min(50, n));
    setSize(clamped);
    setMembers((prev) => {
      const arr = [...prev];
      while (arr.length < clamped) {
        arr.push("");
      }
      arr.length = clamped;
      return arr;
    });
  };

  const submit = () => {
    if (!userId) {
      return toast.error("Sign in required");
    }
    if (!date) {
      return toast.error("Pick a date");
    }
    const cleaned = members.map((m) => m.trim()).filter(Boolean);
    if (size > 1 && cleaned.length < size) {
      return toast.error(`Add all ${size} member names`);
    }
    appendBooking({
      user_id: userId,
      type: "visit",
      booking_date: date,
      booking_time: slot,
      status: "confirmed",
      volunteer_id: null,
      rickshaw_id: null,
      notes: notes || null,
      group_size: size,
      visitor_names: cleaned.join("\n") || null,
    });
    toast.success(isGroup ? "Group visit booked!" : "Visit booked!");
    onCreated();
  };

  return (
    <div className="space-y-4">
      <div
        className="rounded-2xl p-5 text-primary-foreground shadow-md"
        style={{ background: "var(--gradient-sunset)" }}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-90">
              <Sparkles className="h-3 w-3" /> Visit Booking
            </div>
            <h3 className="mt-1 font-serif text-xl font-bold">Plan your darshan</h3>
            <p className="mt-1 text-xs opacity-90">Solo or group. Easy date & time selection.</p>
          </div>
          {isGroup && (
            <span className="rounded-full bg-primary-foreground/20 px-2.5 py-1 text-[10px] font-bold backdrop-blur">
              GROUP
            </span>
          )}
        </div>
      </div>

      <Field label="Visit Date" icon={Calendar}>
        <input
          type="date"
          min={today}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-primary"
        />
      </Field>

      <Field label="Time Slot">
        <div className="grid grid-cols-2 gap-2">
          {SLOTS.map((s) => {
            const active = slot === s.k;
            return (
              <button
                key={s.k}
                type="button"
                onClick={() => setSlot(s.k)}
                className={`rounded-xl border p-3 text-left transition ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
              >
                <div className="text-[12px] font-semibold">{s.label}</div>
                <div className="text-[10px] text-muted-foreground">{s.sub}</div>
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Number of Visitors" icon={Users}>
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-2">
          <button
            type="button"
            onClick={() => syncSize(size - 1)}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted hover:bg-accent"
          >
            <Minus className="h-4 w-4" />
          </button>
          <div className="flex-1 text-center">
            <div className="font-serif text-xl font-bold leading-none">{size}</div>
            <div className="text-[10px] text-muted-foreground">
              {isGroup ? "Group booking" : size > 1 ? "Group" : "Solo"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => syncSize(size + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted hover:bg-accent"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </Field>

      <Field
        label={`Member List ${size > 1 ? `(${size} members)` : "(Primary visitor)"}`}
        icon={Users}
      >
        <div className="space-y-2">
          {members.map((m, i) => (
            <div
              key={i}
              className="flex items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-1.5"
            >
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-primary-foreground"
                style={{ background: "var(--gradient-saffron)" }}
              >
                {i + 1}
              </span>
              <input
                type="text"
                value={m}
                onChange={(e) =>
                  setMembers((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))
                }
                placeholder={i === 0 ? "Primary member name" : `Member ${i + 1} name`}
                className="flex-1 bg-transparent text-sm outline-none"
              />
              {size > 1 && (
                <button
                  type="button"
                  onClick={() => syncSize(size - 1)}
                  className="text-[10px] font-semibold text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          {size < 50 && (
            <button
              type="button"
              onClick={() => syncSize(size + 1)}
              className="flex w-full items-center justify-center gap-1 rounded-xl border border-dashed border-border py-2 text-xs font-semibold text-primary hover:bg-primary/5"
            >
              <Plus className="h-3.5 w-3.5" /> Add Member
            </button>
          )}
        </div>
      </Field>

      <Field label="Notes (optional)" icon={FileText}>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any special requirements..."
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
      </Field>

      <button onClick={submit} className="btn-primary w-full py-3 text-sm font-bold">
        <Check className="h-4 w-4" /> Confirm Visit Booking
      </button>
    </div>
  );
}

function Field({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon?: any;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </label>
      {children}
    </div>
  );
}

/* ---------------- Feeds ---------------- */
function RickshawFeed({
  list,
  onConfirm,
  onNotes,
}: {
  list: Rickshaw[];
  onConfirm: (r: Rickshaw) => void;
  onNotes: (r: Rickshaw) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {list.map((r) => (
        <ProviderCard
          key={r.id}
          name={r.driver_name}
          subtitle={`🛺 ${r.vehicle_number}${r.area ? ` · ${r.area}` : ""}`}
          price={`₹${r.charges_per_trip}/trip`}
          phone={r.phone}
          icon={<Bike className="h-5 w-5 text-primary" />}
          onConfirm={() => onConfirm(r)}
          onNotes={() => onNotes(r)}
        />
      ))}
    </div>
  );
}

function VolunteerFeed({
  list,
  onConfirm,
  onNotes,
}: {
  list: Volunteer[];
  onConfirm: (v: Volunteer) => void;
  onNotes: (v: Volunteer) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {list.map((v) => (
        <ProviderCard
          key={v.id}
          name={v.name}
          subtitle={`${v.languages.join(", ")} · ${v.experience_years}y exp`}
          price={`₹${v.charges_per_day}/day`}
          phone={v.phone}
          icon={<UserCheck className="h-5 w-5 text-primary" />}
          onConfirm={() => onConfirm(v)}
          onNotes={() => onNotes(v)}
        />
      ))}
    </div>
  );
}

function ProviderCard({
  name,
  subtitle,
  price,
  phone,
  icon,
  onConfirm,
  onNotes,
}: {
  name: string;
  subtitle: string;
  price: string;
  phone: string;
  icon: React.ReactNode;
  onConfirm: () => void;
  onNotes: () => void;
}) {
  const initial = name.charAt(0).toUpperCase();
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition hover:shadow-md">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-lg font-bold text-primary-foreground"
            style={{ background: "var(--gradient-saffron)" }}
          >
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="truncate">
                <div className="font-semibold">{name}</div>
                <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{subtitle}</div>
              </div>
              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                {price}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              {icon} <span className="truncate">{phone}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 border-t border-border text-[11px] font-semibold">
        <a
          href={`tel:${phone}`}
          className="flex items-center justify-center gap-1.5 py-2.5 text-primary hover:bg-primary/10"
        >
          <Phone className="h-3.5 w-3.5" /> Call
        </a>
        <button
          onClick={onConfirm}
          className="flex items-center justify-center gap-1.5 border-x border-border py-2.5 text-primary hover:bg-primary/5"
        >
          <Check className="h-3.5 w-3.5" /> Confirm
        </button>
        <button
          onClick={onNotes}
          className="flex items-center justify-center gap-1.5 py-2.5 text-muted-foreground hover:bg-muted"
        >
          <FileText className="h-3.5 w-3.5" /> Notes
        </button>
      </div>
    </div>
  );
}

/* ---------------- Modals ---------------- */
function ModalShell({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center jgy-overlay-scrim p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-border p-5">
          <div>
            {subtitle && (
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {subtitle}
              </div>
            )}
            <div className="font-serif text-lg font-bold">{title}</div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-muted">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        <div className="space-y-3 p-5">{children}</div>
      </div>
    </div>
  );
}

function ConfirmModal({
  userId,
  data,
  onClose,
  onDone,
}: {
  userId?: string;
  data: { kind: "volunteer" | "rickshaw"; id: string; name: string; phone: string };
  onClose: () => void;
  onDone: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("08:00");
  const [notes, setNotes] = useState("");

  const submit = () => {
    if (!userId) {
      return toast.error("Sign in required");
    }
    appendBooking({
      user_id: userId,
      type: data.kind,
      booking_date: date,
      booking_time: time,
      status: "confirmed",
      volunteer_id: data.kind === "volunteer" ? data.id : null,
      rickshaw_id: data.kind === "rickshaw" ? data.id : null,
      notes: notes || null,
      group_size: 1,
      visitor_names: null,
    });
    toast.success("Confirmed!");
    onDone();
  };

  return (
    <ModalShell title={data.name} subtitle={`Confirm ${data.kind}`} onClose={onClose}>
      <Field label="Date" icon={Calendar}>
        <input
          type="date"
          min={today}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Time">
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Notes (optional)" icon={FileText}>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
      </Field>
      <div className="flex gap-2 pt-1">
        <a
          href={`tel:${data.phone}`}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border bg-primary/10 py-2.5 text-xs font-semibold text-primary"
        >
          <Phone className="h-4 w-4" />
          Call
        </a>
        <button onClick={submit} className="btn-primary flex-1 py-2.5 text-xs">
          <Check className="h-4 w-4" /> Confirm
        </button>
      </div>
    </ModalShell>
  );
}

function NotesModal({
  userId,
  data,
  onClose,
  onDone,
}: {
  userId?: string;
  data: { kind: "volunteer" | "rickshaw"; id: string; name: string };
  onClose: () => void;
  onDone: () => void;
}) {
  const [notes, setNotes] = useState("");
  const submit = () => {
    if (!userId) {
      return toast.error("Sign in required");
    }
    if (!notes.trim()) {
      return toast.error("Add a note first");
    }
    appendBooking({
      user_id: userId,
      type: data.kind,
      booking_date: new Date().toISOString().slice(0, 10),
      booking_time: "00:00",
      status: "pending",
      volunteer_id: data.kind === "volunteer" ? data.id : null,
      rickshaw_id: data.kind === "rickshaw" ? data.id : null,
      notes,
      group_size: 1,
      visitor_names: null,
    });
    toast.success("Note saved");
    onDone();
  };
  return (
    <ModalShell title={data.name} subtitle="Add Note" onClose={onClose}>
      <Field label="Remarks / Follow-up" icon={FileText}>
        <textarea
          rows={5}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Special requests, follow-up details..."
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
      </Field>
      <button onClick={submit} className="btn-primary w-full py-2.5 text-xs">
        <Check className="h-4 w-4" />
        Save Note
      </button>
    </ModalShell>
  );
}
